import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import OnboardingTimeline from './components/OnboardingTimeline';
import DocumentChecklist from './components/DocumentChecklist';
import ProgressCard from './components/ProgressCard';
import { merchantsService } from '../../services/supabaseService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const MerchantOnboardingStatus = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [merchant, setMerchant] = useState(null);
  const [onboardingSteps, setOnboardingSteps] = useState([]);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: merchantData } = await merchantsService?.getById(id);

      if (merchantData) {
        setMerchant(merchantData);
        
        // Generate onboarding steps based on merchant status
        const steps = [
          {
            id: 1,
            title: 'Registration',
            description: 'Merchant account created',
            status: 'completed',
            date: merchantData?.registrationDate,
            icon: 'UserPlus'
          },
          {
            id: 2,
            title: 'Business Verification',
            description: 'Business documents submitted and verified',
            status: merchantData?.status === 'pending' ? 'in_progress' : 'completed',
            date: merchantData?.status !== 'pending' ? merchantData?.registrationDate : null,
            icon: 'FileCheck'
          },
          {
            id: 3,
            title: 'Payment Setup',
            description: 'Payment methods and gateway configuration',
            status: merchantData?.status === 'active' ? 'completed' : merchantData?.status === 'pending' ? 'pending' : 'completed',
            date: merchantData?.status === 'active' ? merchantData?.activationDate : null,
            icon: 'CreditCard'
          },
          {
            id: 4,
            title: 'Activation',
            description: 'Merchant account activated and ready',
            status: merchantData?.status === 'active' ? 'completed' : 'pending',
            date: merchantData?.activationDate,
            icon: 'CheckCircle'
          }
        ];
        setOnboardingSteps(steps);

        // Generate document checklist
        const docs = [
          { id: 1, name: 'Business Registration Certificate', status: merchantData?.status !== 'pending' ? 'verified' : 'pending', icon: 'FileText' },
          { id: 2, name: 'Tax Identification Number (NPWP)', status: merchantData?.status !== 'pending' ? 'verified' : 'pending', icon: 'FileText' },
          { id: 3, name: 'Bank Account Details', status: merchantData?.status === 'active' ? 'verified' : 'pending', icon: 'Building' },
          { id: 4, name: 'Identity Verification (KTP)', status: merchantData?.status !== 'pending' ? 'verified' : 'pending', icon: 'CreditCard' },
          { id: 5, name: 'Business Address Proof', status: merchantData?.status === 'active' ? 'verified' : 'pending', icon: 'MapPin' }
        ];
        setDocuments(docs);
      }
    } catch (error) {
      console.error('Error fetching merchant onboarding data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    const completedSteps = onboardingSteps?.filter(s => s?.status === 'completed')?.length || 0;
    const totalSteps = onboardingSteps?.length || 1;
    return Math.round((completedSteps / totalSteps) * 100);
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Onboarding Status | PaymentNexus</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 h-96 bg-muted rounded-xl"></div>
                <div className="h-96 bg-muted rounded-xl"></div>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  if (!merchant) {
    return (
      <>
        <Helmet>
          <title>Merchant Not Found | PaymentNexus</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={64} className="mx-auto text-muted-foreground mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Merchant Not Found</h2>
              <p className="text-muted-foreground mb-6">The merchant you're looking for doesn't exist</p>
              <Button onClick={() => navigate('/merchants')}>Back to Merchants</Button>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{merchant?.merchantName} - Onboarding Status | PaymentNexus</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/merchants/${id}`)}
                className="flex items-center gap-2"
              >
                <Icon name="ArrowLeft" size={16} />
                Back
              </Button>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">Onboarding Status</h1>
                <p className="text-muted-foreground">{merchant?.merchantName}</p>
              </div>
            </div>
          </div>

          {/* Progress Overview */}
          <ProgressCard progress={calculateProgress()} merchant={merchant} />

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Onboarding Timeline */}
            <div className="lg:col-span-2">
              <OnboardingTimeline steps={onboardingSteps} />
            </div>

            {/* Document Checklist */}
            <div>
              <DocumentChecklist documents={documents} />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default MerchantOnboardingStatus;